// Movorra Live — operator console. A flow's `human://handoff` action queues a conversation here; the operator
// claims it (first-come), chats with the customer, and hands back to the bot. The queue refreshes every 8s and
// new events arrive over a long-poll (/studio/api/live/poll) — not SSE. Operator replies reach a customer only
// on a client that polls for them (the embed widget does; the /r3ride page does not).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PageHeader, Card, Button, Alert } from '../ui/primitives';
import { liveQueue, getLive, claimLive, sendLive, handbackLive, liveLongPoll, suggestLive, sessionAttachments, attachmentUrl, sendLiveFile } from '../services/ecapApi';
import CapabilityNotice from '../components/CapabilityNotice';

const fmtTime = (ms) => (ms ? new Date(ms).toLocaleTimeString() : '');
// Transcript lines are stored as "role: text"; split once so operator/customer/bot render distinctly.
function parseLine(line) {
  const i = line.indexOf(': ');
  if (i < 0) return { role: 'bot', text: line };
  const role = line.slice(0, i).toLowerCase();
  return { role, text: line.slice(i + 2) };
}

function Bubble({ role, text }) {
  const mine = role === 'operator';
  const tone = mine ? 'bg-blue-600 text-white' : role === 'customer' ? 'bg-slate-100 text-slate-800' : 'bg-emerald-50 text-emerald-800 border border-emerald-100';
  const who = role === 'operator' ? 'You' : role === 'customer' ? 'Customer' : 'Assistant';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'} mb-1.5`}>
      <div className={`max-w-[78%] rounded-2xl px-3 py-1.5 text-sm ${tone}`}>
        <div className="text-[10px] opacity-60 mb-0.5">{who}</div>{text}
      </div>
    </div>
  );
}

export default function OpsLive({ authSession, capability }) {
  const [waiting, setWaiting] = useState([]);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);     // every active conversation across operators (supervision) — BUG-48
  const [ended, setEnded] = useState([]); // closed conversations (history) — BUG-48
  const [active, setActive] = useState(null); // { conversation, lines: [] }
  const [popup, setPopup] = useState(null); // newest handoff.requested
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);
  const [unread, setUnread] = useState({}); // sessionId -> count of unseen customer messages
  const [files, setFiles] = useState([]); // files the customer shared in the open conversation
  const [filesError, setFilesError] = useState(false); // OPSLIVE-FILESTRIP: the file listing failed to load
  const [transcriptStale, setTranscriptStale] = useState(false); // OPSLIVE-TRANSCRIPT: poll has failed repeatedly
  const pollFailRef = useRef(0); // consecutive transcript-poll failures
  const activeSidRef = useRef(null); // current open session id (ref, so the long-poll closure isn't stale)
  const listRef = useRef(null);      // the scrolling transcript pane
  const pinnedRef = useRef(true);    // operator is following the bottom (false once they scroll up to read)
  const selectionRef = useRef(null); // opslive-openconv-load-race: the conversation the operator most recently chose
  const openingRef = useRef(null);   // opslive-claim-double-submit: conv ids with an open/claim POST in flight
  if (openingRef.current === null) openingRef.current = new Set();
  const [handingBack, setHandingBack] = useState(false); // opslive-handback-double-submit
  const activeId = active ? active.conversation.id : null;
  const activeSid = active ? active.conversation.sessionId : null;

  const reload = useCallback(async () => {
    try { const q = await liveQueue(authSession); setWaiting(q.waiting || []); setMine(q.mine || []); setAll(q.all || []); setEnded(q.ended || []); }
    catch (e) { setError(e.message); }
  }, [authSession]);

  // Near-instant via LONG-POLL (API Gateway can't serve SSE): a request that holds ~25s and returns on the
  // first live event, then re-requests immediately. A slow short-poll is the safety net for any gap.
  useEffect(() => {
    reload();
    const poll = setInterval(reload, 8000);
    let stopped = false;
    const loop = async () => {
      while (!stopped) {
        try {
          const r = await liveLongPoll(authSession);
          if (stopped) break;
          if (r && r.event && r.event !== 'timeout') {
            if (r.event === 'handoff.requested') setPopup(r.data);
            if (r.event === 'chat.customer_message') {
              const sid = r.data && r.data.sessionId;
              if (sid && sid !== activeSidRef.current) setUnread((u) => ({ ...u, [sid]: (u[sid] || 0) + 1 }));
            }
            reload();
          }
        } catch (e) { if (stopped) break; await new Promise((res) => setTimeout(res, 2000)); }
      }
    };
    loop();
    return () => { stopped = true; clearInterval(poll); };
  }, [reload, authSession]);

  // Poll the open conversation's transcript (authoritative) so an operator sees new customer messages
  // within a few seconds even when the live stream is unavailable; self-heals any optimistic drift.
  // Keyed on the open conversation's ID — NOT the `active` object, whose identity this very effect
  // changes on each tick (that would tear down and restart the timer in a tight fetch loop).
  useEffect(() => {
    if (!activeId) { setFiles([]); setFilesError(false); setTranscriptStale(false); pollFailRef.current = 0; return undefined; }
    const id = activeId;
    const sid = activeSid;
    let alive = true;
    const tick = async () => {
      try {
        const full = await getLive(id, authSession);
        if (alive && full) setActive((a) => (a && a.conversation.id === id
          ? { ...a, conversation: full, lines: (full.transcript || []).map(parseLine) } : a));
        pollFailRef.current = 0; // OPSLIVE-TRANSCRIPT: a good poll clears the staleness flag
        if (alive) setTranscriptStale(false);
      } catch (e) {
        // OPSLIVE-TRANSCRIPT-SILENT-FREEZE: don't silently swallow — after a few consecutive failures flag the
        // transcript as stale so the operator knows they may be looking at a frozen conversation.
        pollFailRef.current += 1;
        if (alive && pollFailRef.current >= 3) setTranscriptStale(true);
      }
      // Files the customer shared appear here within a few seconds (public runtime endpoint).
      if (sid) {
        // OPSLIVE-FILESTRIP: distinguish "no files" from "couldn't load" — keep the prior list and flag the error.
        try { const fs = await sessionAttachments(sid); if (alive) { setFiles(fs); setFilesError(false); } }
        catch (e) { if (alive) setFilesError(true); }
      }
    };
    tick();
    const h = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(h); };
  }, [activeId, activeSid, authSession]);

  // Opening a different chat starts at the newest message.
  useEffect(() => {
    activeSidRef.current = activeSid;
    pinnedRef.current = true;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeId, activeSid]);

  // New lines follow the bottom ONLY while the operator hasn't scrolled up — otherwise reading back
  // through the transcript is impossible, since the 3s transcript poll would keep dragging them down.
  const lineCount = active ? active.lines.length : 0;
  useEffect(() => {
    const el = listRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [lineCount]);

  // Scrolling within ~60px of the bottom re-arms auto-follow.
  const onTranscriptScroll = () => {
    const el = listRef.current;
    if (el) pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  // readOnly = a supervision/history view (another operator's active chat, or an ended one) — the panel opens
  // immediately (even if the transcript is empty/stale) and the composer is disabled (BUG-48 follow-up: clicking
  // "Other operators"/"Ended" previously could show a blank pane with an editable composer).
  const openConversation = async (conv, doClaim, readOnly = false) => {
    setError(null);
    // opslive-claim-double-submit: claimLive is NOT idempotent — a double-click would fire two claim POSTs
    // (and race). Ignore a repeat open of the same conversation while one is already in flight.
    if (openingRef.current.has(conv.id)) return;
    // opslive-openconv-load-race: record the operator's LATEST choice. The header/composer and send() all key
    // off `active`, so if a slower earlier getLive/claimLive resolves after a newer selection and overwrites
    // `active`, the operator's typed reply is delivered to the WRONG customer. Drop any stale response whose
    // conversation is no longer the current selection.
    selectionRef.current = conv.id;
    if (readOnly) setActive({ conversation: conv, lines: (conv.transcript || []).map(parseLine), readOnly: true });
    openingRef.current.add(conv.id);
    try {
      const full = doClaim ? await claimLive(conv.id, authSession) : await getLive(conv.id, authSession);
      if (selectionRef.current !== conv.id) return; // superseded by a newer open — drop this stale response
      setActive({ conversation: full, lines: (full.transcript || []).map(parseLine), readOnly });
      setUnread((u) => ({ ...u, [full.sessionId]: 0 })); // opening a chat clears its unread
      setPopup((p) => (p && p.id === conv.id ? null : p));
      reload();
    } catch (e) {
      if (selectionRef.current === conv.id && !readOnly) { setError(e.message || 'Could not open — it may have just been claimed.'); }
      reload();
    } finally {
      openingRef.current.delete(conv.id);
    }
  };

  const send = async () => {
    if (!draft.trim() || !active) return;
    const text = draft.trim(); setDraft('');
    pinnedRef.current = true; // sending always jumps back to the newest message
    const optimistic = { role: 'operator', text };
    setActive((a) => ({ ...a, lines: [...a.lines, optimistic] })); // optimistic
    try { await sendLive(active.conversation.id, text, authSession); }
    catch (e) {
      // OPSLIVE-SEND-LOST-MESSAGE: the send failed — the customer never got it. Remove the phantom
      // "delivered" bubble and restore the operator's text to the composer so it isn't silently lost.
      setError(e.message);
      setActive((a) => (a ? { ...a, lines: a.lines.filter((l) => l !== optimistic) } : a));
      setDraft((d) => (d ? d : text));
    }
  };

  const [suggesting, setSuggesting] = useState(false);
  const suggest = async () => {
    if (!active) return;
    setSuggesting(true);
    try { const r = await suggestLive(active.conversation.id, authSession); if (r?.suggestion) setDraft(r.suggestion); }
    catch (e) { setError(e.message); }
    finally { setSuggesting(false); }
  };

  const fileRef = useRef(null);
  const attach = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !active) return;
    // The upload is what matters; the re-fetch is just to show it immediately (the 3s poll will catch up
    // anyway), so a re-fetch failure must NOT surface as an upload failure now that sessionAttachments throws.
    try {
      await sendLiveFile(active.conversation.id, file, authSession);
      try { const fs = await sessionAttachments(active.conversation.sessionId); setFiles(fs); setFilesError(false); }
      catch (re) { setFilesError(true); }
    } catch (err) {
      // opslive-sendfile-opaque-status: give actionable guidance on the common failure (too large) now that
      // sendLiveFile carries the HTTP status.
      setError(err.status === 413 ? 'That file is too large to share — try a smaller file.' : err.message);
    }
  };

  const handback = async () => {
    // opslive-handback-double-submit: guard against a double-click firing two handback POSTs.
    if (!active || handingBack) return;
    setHandingBack(true);
    try { await handbackLive(active.conversation.id, '', authSession); setActive(null); reload(); }
    catch (e) { setError(e.message); }
    finally { setHandingBack(false); }
  };

  const totalUnread = mine.reduce((n, c) => n + (unread[c.sessionId] || 0), 0);
  return (
    <div className="p-6">
      <PageHeader title={totalUnread > 0 ? `Live · ${totalUnread} new` : 'Live'} subtitle="Real-time human handoff — claim a waiting chat and talk to the customer"
        right={<Button size="sm" variant="outline" onClick={reload}>Refresh</Button>} />
      <CapabilityNotice capability={capability} kind="liveHandoff" />

      {popup && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-3">
          <span className="text-sm text-amber-800">🔔 New chat waiting — <b>{popup.title || 'customer needs a human'}</b></span>
          <Button size="sm" onClick={() => openConversation(popup, true)}>Pick</Button>
          <button className="text-amber-500 text-xs ml-auto" onClick={() => setPopup(null)}>dismiss</button>
        </div>
      )}
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}

      <div className="grid md:grid-cols-3 gap-4">
        {/* Left: queue */}
        <div className="md:col-span-1 space-y-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Waiting ({waiting.length})</div>
            {waiting.length === 0 && <div className="text-sm text-slate-400">No one waiting.</div>}
            {waiting.map((c) => (
              <Card key={c.id} className="mb-2">
                <div className="text-sm font-medium text-slate-800 truncate">{c.title || c.sessionId}</div>
                <div className="text-[11px] text-slate-400 mb-1">{c.flowId} · {fmtTime(c.createdAt)}</div>
                <Button size="sm" onClick={() => openConversation(c, true)}>Pick</Button>
              </Card>
            ))}
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
              My chats ({mine.length}){totalUnread > 0 && <span className="ml-1 text-rose-600">· {totalUnread} unread</span>}
            </div>
            {mine.map((c) => {
              const u = unread[c.sessionId] || 0;
              return (
                <button key={c.id} onClick={() => openConversation(c, false)}
                  className={`block w-full text-left rounded-lg border px-3 py-2 mb-2 flex items-center gap-2 ${active?.conversation.id === c.id ? 'border-blue-400 bg-blue-50' : u > 0 ? 'border-rose-300 bg-rose-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">{c.title || c.sessionId}</div>
                    <div className="text-[11px] text-slate-400">{c.flowId}</div>
                  </div>
                  {u > 0 && <span className="shrink-0 min-w-[20px] text-center text-[11px] font-semibold text-white bg-rose-500 rounded-full px-1.5 py-0.5">{u}</span>}
                </button>
              );
            })}
          </div>

          {/* BUG-48: conversations claimed by OTHER operators — supervision (a super-admin/supervisor can see
              handoffs they don't own, which the old waiting/mine-only console hid). */}
          {(() => {
            const others = (all || []).filter((c) => c.state === 'CLAIMED' && !mine.some((m) => m.id === c.id));
            if (others.length === 0) return null;
            return (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Other operators ({others.length})</div>
                {others.map((c) => (
                  <button key={c.id} onClick={() => openConversation(c, false, true)}
                    className="block w-full text-left rounded-lg border border-slate-200 hover:bg-slate-50 px-3 py-2 mb-2">
                    <div className="text-sm font-medium text-slate-800 truncate">{c.title || c.sessionId}</div>
                    <div className="text-[11px] text-slate-400">{c.flowId} · 👤 {c.assignedOperator || 'claimed'}</div>
                  </button>
                ))}
              </div>
            );
          })()}

          {/* BUG-48: ended conversations (history) — review a handoff after it closes. Read-only transcript. */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Ended ({ended.length})</div>
            {ended.length === 0 && <div className="text-sm text-slate-400">No ended conversations.</div>}
            {ended.slice(0, 30).map((c) => (
              <button key={c.id} onClick={() => openConversation(c, false, true)}
                className="block w-full text-left rounded-lg border border-slate-200 hover:bg-slate-50 px-3 py-2 mb-2 opacity-80">
                <div className="text-sm font-medium text-slate-700 truncate">{c.title || c.sessionId}</div>
                <div className="text-[11px] text-slate-400">{c.flowId} · ended {fmtTime(c.endedAt || c.createdAt)}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: active chat */}
        <div className="md:col-span-2">
          {!active ? (
            <div className="text-sm text-slate-400 py-16 text-center border border-dashed border-slate-200 rounded-xl">
              Pick a waiting chat to start talking to the customer.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl flex flex-col h-[70vh]">
              <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
                <div className="text-sm font-semibold text-slate-800 truncate">{active.conversation.title || active.conversation.sessionId}</div>
                {active.readOnly
                  ? <span className="ml-auto text-[11px] font-medium text-slate-500 bg-slate-100 rounded px-2 py-1">👁 Read-only{active.conversation.state === 'ENDED' ? ' · ended' : ` · ${active.conversation.assignedOperator || 'another operator'}`}</span>
                  : <Button size="sm" variant="outline" className="ml-auto" onClick={handback} disabled={handingBack}>{handingBack ? 'Handing back…' : 'Hand back to bot'}</Button>}
              </div>
              {active.conversation.aiRecommendation && (
                <div className="px-3 py-1.5 text-[12px] text-indigo-700 bg-indigo-50 border-b border-indigo-100">🧠 {active.conversation.aiRecommendation}</div>
              )}
              {files.length > 0 && (
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex flex-wrap gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 self-center">📎 Files</span>
                  {files.map((f) => {
                    const url = attachmentUrl(active.conversation.sessionId, f.id);
                    const isImg = (f.contentType || '').startsWith('image/');
                    return (
                      <a key={f.id} href={url} target="_blank" rel="noreferrer" title={f.filename}
                        className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[12px] text-slate-700 hover:border-blue-400">
                        {isImg
                          ? <img src={url} alt={f.filename} className="w-8 h-8 object-cover rounded" />
                          : <span className="text-base">📄</span>}
                        <span className="max-w-[140px] truncate">{f.filename}</span>
                      </a>
                    );
                  })}
                </div>
              )}
              {filesError && (
                <div className="px-3 py-1.5 border-b border-amber-100 bg-amber-50 text-[11px] text-amber-700">⚠ Couldn't load the customer's shared files — retrying. Any files they sent may not be shown yet.</div>
              )}
              {transcriptStale && (
                <div className="px-3 py-1.5 border-b border-amber-100 bg-amber-50 text-[11px] text-amber-700">⚠ Reconnecting… this transcript may be out of date (the live updates stopped responding).</div>
              )}
              <div ref={listRef} onScroll={onTranscriptScroll} className="flex-1 overflow-y-auto p-3">
                {active.lines.length === 0
                  ? <div className="text-sm text-slate-400 text-center py-10">No messages in this conversation.</div>
                  : active.lines.map((l, i) => <Bubble key={i} role={l.role} text={l.text} />)}
              </div>
              {active.readOnly ? (
                <div className="border-t border-slate-100 p-2 text-center text-[12px] text-slate-400">
                  You're viewing this conversation read-only. {active.conversation.state === 'ENDED' ? 'It has ended.' : 'Another operator is handling it.'}
                </div>
              ) : (
                <div className="border-t border-slate-100 p-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={suggest} disabled={suggesting} title="AI-drafted reply from the conversation">{suggesting ? '…' : '✨'}</Button>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={attach} />
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} title="Share a file with the customer">📎</Button>
                  <input className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm" value={draft}
                    onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                    placeholder="Type a message to the customer…" />
                  <Button size="sm" onClick={send} disabled={!draft.trim()}>Send</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
